import { AppointmentType } from '../constants'
import { TrialGateReasons } from '../hooks/useBlockedAppointmentType'
import { AppointmentTypes } from '../types'
import { appointmentTypeToDisplayText, appointmentTypeToPriority } from './appointmentTypeToDisplayText'

type GatedAppointmentTypeOption = {
  disabled: boolean
  name: string
  priority: number
  reason: string | undefined
  value: AppointmentType
}

type ToGatedAppointmentTypeOptionsParameters = {
  appointmentTypes: AppointmentTypes | undefined
  blockedAppointmentTypes: AppointmentType[]
  reasons: TrialGateReasons
  shouldRestrictToHAE: boolean
}

/**
 * The appointment-type dropdown options, with every type the trial gate blocks rendered disabled and
 * carrying its own reason copy. Shared by the two surfaces that offer the type as a dropdown (the
 * schedule form and the consultant route's configuration panel) so the option shape, the
 * HAE restriction and the priority ordering cannot drift apart between them - and so the
 * `offeredAppointmentTypes` each one reports to useTrialGateTracking stays the same measurement.
 *
 * Takes a set and a per-type reason map rather than one of each: a single trial decision can block
 * more than one type, and those types do not read the same to the user (PX-3749).
 */
const toGatedAppointmentTypeOptions = ({
  appointmentTypes,
  blockedAppointmentTypes,
  reasons,
  shouldRestrictToHAE,
}: ToGatedAppointmentTypeOptionsParameters): GatedAppointmentTypeOption[] =>
  Object.values(appointmentTypes ?? ({} as AppointmentTypes))
    .map((config) => ({
      disabled: blockedAppointmentTypes.includes(config.appointment_type),
      name: appointmentTypeToDisplayText[config.appointment_type],
      priority: appointmentTypeToPriority[config.appointment_type],
      reason: reasons[config.appointment_type],
      value: config.appointment_type,
    }))
    .filter((option) => (shouldRestrictToHAE ? option.value === AppointmentType.HEARING_AID_EVALUATION : true))
    .sort((a, b) => a.priority - b.priority)

export { toGatedAppointmentTypeOptions }
export type { GatedAppointmentTypeOption }
